import { DestroyRef, Injectable, inject } from '@angular/core';
import { ChildrenOutletContexts, Router } from '@angular/router';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { Observable, firstValueFrom, isObservable } from 'rxjs';
import { CanDeactivateGuard } from 'src/app/guards/confirm-unsaved-changes.guard';
import { PullRefreshRouteReuseStrategy } from './pull-refresh-route-reuse.strategy';
import { HttpActivityService } from 'src/app/services/http-activity.service';
import { PULL_REFRESH_BLOCKED, PULL_REFRESH_FORM_LIKE } from './pull-refresh-blocklist';

/** What a page does when the user pulls down to refresh. It may return nothing, a Promise or an Observable;
 *  the spinner stays until it finishes (and at least a moment, so a quick reload still shows a response). */
export type PullRefreshHandler = () => void | Promise<unknown> | Observable<unknown>;

/**
 * Pull to refresh — one gesture for the whole app (see PullToRefreshComponent, mounted once in the app shell).
 *
 * What "refresh" means, in order:
 *  1. The page registered its own refresh (fastest, keeps filters and page; used by lists and dashboards):
 *       private readonly pullRefresh = inject(PullToRefreshService).attach(() => this.load());
 *     It unregisters itself when the page is destroyed. If several are registered, the latest wins.
 *  2. Otherwise the page is rebuilt by the router (fresh data, same url), unless the screen is listed in
 *     pull-refresh-blocklist.ts or its route has `data: { pullRefresh: false }`. Before rebuilding, the same
 *     "unsaved changes" question the route guard asks is asked of the open page (CanDeactivateGuard); if the
 *     person chooses Stay, nothing reloads.
 *
 * Deliberately NO overscroll-behavior on <html>: on iPhone Safari it made the fixed footer scroll with the page.
 * The gesture itself stops the browser's own pull-to-reload / bounce while a pull is in progress.
 */
@Injectable({ providedIn: 'root' })
export class PullToRefreshService {
  private readonly router = inject(Router);
  private readonly contexts = inject(ChildrenOutletContexts);
  private readonly leaveGuard = inject(CanDeactivateGuard);
  private readonly reuse = inject(PullRefreshRouteReuseStrategy);
  private readonly activity = inject(HttpActivityService);

  private stack: PullRefreshHandler[] = [];

  /** Registers the handler and removes it automatically when the calling component is destroyed.
   *  Must be called in an injection context (constructor / field initializer). */
  attach(handler: PullRefreshHandler): () => void {
    const unregister = this.register(handler);
    inject(DestroyRef).onDestroy(unregister);
    return unregister;
  }

  register(handler: PullRefreshHandler): () => void {
    this.stack.push(handler);
    return () => {
      const i = this.stack.lastIndexOf(handler);
      if (i >= 0) this.stack.splice(i, 1);
    };
  }

  /** True when a pull can do something on the current screen. */
  get enabled(): boolean {
    // never while a file is being uploaded (a reload or rebuild would cut it off)
    if (this.activity.uploadsInProgress) return false;
    return this.stack.length > 0 || this.canRebuildHere();
  }

  /** @deprecated use `enabled` */
  get hasHandler(): boolean {
    return this.enabled;
  }

  /** Runs the refresh for the current screen; resolves when it is done (never rejects). */
  async run(): Promise<void> {
    const started = Date.now();
    const handler = this.stack[this.stack.length - 1];
    try {
      if (handler) {
        const result = handler();
        if (isObservable(result)) await firstValueFrom(result, { defaultValue: undefined });
        else await result;
      } else {
        await this.rebuildCurrentPage();
      }
    } catch {
      /* the page shows its own error; the pull just ends */
    }
    const left = 800 - (Date.now() - started);
    if (left > 0) await new Promise<void>((resolve) => setTimeout(resolve, left));
  }

  // ── default: rebuild the current page ─────────────────────────────────────────────────────────

  private routeChain(): ActivatedRouteSnapshot[] {
    const chain: ActivatedRouteSnapshot[] = [];
    let r: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (r?.firstChild) {
      r = r.firstChild;
      chain.push(r);
    }
    return chain;
  }

  private canRebuildHere(): boolean {
    if (this.router.currentNavigation()) return false;
    const path = this.router.url.split(/[?#]/)[0];
    if (!path || PULL_REFRESH_BLOCKED.some((re) => re.test(path))) return false;
    const chain = this.routeChain();
    if (!chain.length) return false;
    if (chain.some((s) => s.data?.['pullRefresh'] === false)) return false;
    if (PULL_REFRESH_FORM_LIKE.some((re) => re.test(path)) && !chain.some((s) => !!s.routeConfig?.canDeactivate?.length)) return false;
    return true;
  }

  /** The components currently showing in the router outlets, outermost first. */
  private activeComponents(): unknown[] {
    const out: unknown[] = [];
    let ctx: ChildrenOutletContexts | null = this.contexts;
    for (;;) {
      const c = ctx?.getContext('primary');
      if (!c?.outlet?.isActivated) break;
      out.push(c.outlet.component);
      ctx = c.children;
    }
    return out;
  }

  private async rebuildCurrentPage(): Promise<void> {
    if (!this.canRebuildHere()) return;
    // the same question the route guard asks when you leave a page with unsaved changes
    for (const component of this.activeComponents()) {
      const answer = this.leaveGuard.canDeactivate(component as never);
      const ok = isObservable(answer) ? await firstValueFrom(answer, { defaultValue: false }) : await answer;
      if (!ok) return; // chose Stay
    }
    this.reuse.rebuildNext = true;
    try {
      await this.router.navigateByUrl(this.router.url, { onSameUrlNavigation: 'reload', skipLocationChange: true });
    } finally {
      this.reuse.rebuildNext = false;
    }
  }
}
